import { UpdateCardLinkListItem } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/UpdateCardLinkListItem';
import { CardLink } from '@/types/card';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface UpdateCardLinkListItemsProps {
  links: CardLink[];
  onLinkClick: (linkId: number) => void;
  showDirectOption: boolean;
  updateLinks: (updatedLinks: CardLink[]) => void;
  setHasDirectLink?: (hasDirectLink: boolean) => void;
}

export const UpdateCardLinkListItems = ({
  links,
  onLinkClick,
  showDirectOption,
  updateLinks,
  setHasDirectLink,
}: UpdateCardLinkListItemsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleToggleActive = (id: number, isActive: boolean) => {
    const updatedLinks = links.map(link =>
      link.id === id ? { ...link, is_active: isActive } : link,
    );

    updateLinks(updatedLinks);
  };

  const handleSetDirect = (id: number) => {
    const updatedLinks = links.map(link => ({
      ...link,
      is_active: true,
      is_direct: link.id === id,
    }));

    updateLinks(updatedLinks);

    if (setHasDirectLink) {
      setHasDirectLink(true);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = links.findIndex(link => `link-${link.id}` === active.id);
    const newIndex = links.findIndex(link => `link-${link.id}` === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newLinks = arrayMove(links, oldIndex, newIndex);

      const updatedLinks = newLinks.map((link, index) => ({
        ...link,
        order: index + 1,
      }));

      updateLinks(updatedLinks);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
    >
      <SortableContext
        items={links.map(link => `link-${link.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="relative w-full select-none space-y-3 overflow-hidden py-2">
          {links.map(link => (
            <UpdateCardLinkListItem
              key={link.id}
              link={link}
              onToggleActive={handleToggleActive}
              onSetDirect={handleSetDirect}
              showDirectOption={showDirectOption}
              onClick={onLinkClick}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
